
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Logo from './Logo';
import { Facebook, Twitter, Instagram, Youtube } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);

  const handleHowItWorksClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname === '/') {
      // If already on home page, just scroll
      document.querySelector('#how-it-works')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Navigate to home page, then scroll
      navigate('/#how-it-works');
      setTimeout(() => {
        document.querySelector('#how-it-works')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const handleNewsletterSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newsletterEmail || !newsletterEmail.includes('@')) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address",
        variant: "destructive"
      });
      return;
    }

    setIsSubscribing(true);

    try {
      const response = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: newsletterEmail,
          source: 'website_footer'
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: "Successfully subscribed!",
          description: "Check your email for a welcome message from Dine AI",
        });
        setNewsletterEmail(''); // Clear input
      } else {
        throw new Error(data.detail || 'Subscription failed');
      }
    } catch (error) {
      toast({
        title: "Subscription failed",
        description: "Please try again later or contact us directly",
        variant: "destructive"
      });
    } finally {
      setIsSubscribing(false);
    }
  };
  
  return (
    <footer className="bg-gradient-to-b from-dineai-blue/95 to-dineai-blue pt-20 pb-8 relative overflow-hidden">
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1555396273-367ea4eb4db5?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80')] opacity-5 bg-cover bg-fixed"></div>
      
      <div className="absolute -top-12 left-0 right-0">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" className="text-white/5 fill-current w-full">
          <path d="M0,192L60,170.7C120,149,240,107,360,117.3C480,128,600,192,720,202.7C840,213,960,171,1080,144C1200,117,1320,107,1380,101.3L1440,96L1440,320L1380,320C1320,320,1200,320,1080,320C960,320,840,320,720,320C600,320,480,320,360,320C240,320,120,320,60,320L0,320Z"></path>
        </svg>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
          <div className="md:col-span-5">
            <Logo variant="light" />
            <p className="text-white/80 mt-6 max-w-md leading-relaxed">
              Dine AI simplifies the dining experience for both restaurants and customers through a scalable,
              user-friendly platform that leverages AI and personalization to transform how people discover, explore, and enjoy dining out.
            </p>
            
            <div className="mt-6 flex space-x-4">
              <a href="https://www.facebook.com/dinewithai" target="_blank" rel="noopener noreferrer" className="bg-white/10 hover:bg-white/20 p-3 rounded-full transition-colors">
                <Facebook className="w-5 h-5 text-white" />
              </a>
              <a href="https://x.com/dinewithai" target="_blank" rel="noopener noreferrer" className="bg-white/10 hover:bg-white/20 p-3 rounded-full transition-colors">
                <Twitter className="w-5 h-5 text-white" />
              </a>
              <a href="https://www.instagram.com/dineai.ai/" target="_blank" rel="noopener noreferrer" className="bg-white/10 hover:bg-white/20 p-3 rounded-full transition-colors">
                <Instagram className="w-5 h-5 text-white" />
              </a>
              <a href="https://www.youtube.com/@DineAI" target="_blank" rel="noopener noreferrer" className="bg-white/10 hover:bg-white/20 p-3 rounded-full transition-colors">
                <Youtube className="w-5 h-5 text-white" />
              </a>
            </div>
          </div>
          
          <div className="md:col-span-2">
            <h3 className="text-dineai-yellow font-semibold mb-4 text-lg">Products</h3>
            <ul className="space-y-3">
              <li><Link to="/products/virtual-host" className="text-white/80 hover:text-white transition-colors">AI Virtual Host</Link></li>
              <li><Link to="/products/virtual-assistant" className="text-white/80 hover:text-white transition-colors">AI Virtual Assistant</Link></li>
              <li><a href="/#how-it-works" onClick={handleHowItWorksClick} className="text-white/80 hover:text-white transition-colors cursor-pointer">How It Works</a></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <h3 className="text-dineai-yellow font-semibold mb-4 text-lg">Solutions</h3>
            <ul className="space-y-3">
              <li><Link to="/restaurants" className="text-white/80 hover:text-white transition-colors">For Restaurants</Link></li>
              <li><Link to="/customers" className="text-white/80 hover:text-white transition-colors">For Customers</Link></li>
              <li><Link to="/enterprise" className="text-white/80 hover:text-white transition-colors">For Enterprises</Link></li>
              <li><Link to="/case-studies" className="text-white/80 hover:text-white transition-colors">Case Studies</Link></li>
              <li><Link to="/blog" className="text-white/80 hover:text-white transition-colors">Blog</Link></li>
              <li><Link to="/faq" className="text-white/80 hover:text-white transition-colors">FAQ</Link></li>
            </ul>
          </div>
          
          <div className="md:col-span-3">
            <h3 className="text-dineai-yellow font-semibold mb-4 text-lg">Download</h3>
            <ul className="space-y-3">
              <li><a href="https://apps.apple.com/us/app/dine-ai/id6743492809" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white transition-colors flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.05 20.28c-.98.95-2.05.96-3.08.44-1.07-.54-2.13-.52-3.25 0-1.44.73-2.21.53-3.1-.44C2.32 14.85 3.07 6.67 8.28 6.31c1.66.07 2.81 1.12 3.72 1.12.89 0 2.37-1.38 4.28-1.17 1.58.12 2.78.73 3.58 1.86-3.25 1.95-2.72 6.21.4 7.47-.95 2.06-2.18 4.1-3.21 4.69z" />
                  <path d="M13.33 5.33c.86-1.1 1.51-2.62 1.28-4.2-1.4.09-3.03.95-3.98 2.13-.86 1.05-1.57 2.61-1.3 4.13 1.53.12 3.01-.83 4-2.06z" />
                </svg>
                App Store
              </a></li>
              <li><a href="https://play.google.com/store/apps/details?id=com.dine.ai&hl=en_US" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white transition-colors flex items-center">
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.9 5C17.9 5 13.65 8.1 12 9.55C10.34 11.01 7.9 14.03 7.9 14.03C7.9 14.03 6.91 15.5 8.9 15.5C10.89 15.5 12 13.46 12 13.46L17.5 7.35C17.5 7.35 18.74 6 17.9 5Z" />
                  <path d="M5.89999 9.33996C7.69999 7.53996 9.40001 5.6 9.40001 5.6C9.40001 5.6 10.98 3.83996 12.8 5.66996C14.62 7.49996 12.8 9.33996 12.8 9.33996L8.20001 13.94C8.20001 13.94 7.60001 14.54 6.90001 13.84C6.20001 13.14 6.90001 12.44 6.90001 12.44L11.5 7.83996C11.5 7.83996 11.9 7.43996 11.5 7.03996C11.1 6.63996 10.7 7.03996 10.7 7.03996L6.59998 11.14C6.59998 11.14 5.29999 12.44 6.59998 13.74C7.89998 15.04 9.20001 13.84 9.20001 13.84L14.5 8.53996C14.5 8.53996 16.5 6.83996 14.5 4.83996C12.5 2.83996 10.8 4.53996 10.8 4.53996C10.8 4.53996 8.09999 7.13996 6.29999 8.93996C6.29999 8.93996 5.5 9.63996 5.5 10.54C5.5 11.16 5.83333 11.62 6 11.84" />
                </svg>
                Google Play
              </a></li>
              <li className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="w-20 h-20 bg-white rounded-lg p-1.5 flex-shrink-0">
                    <img
                      src="/images/qr-codes/qr-code.svg"
                      alt="Scan to Download Dine AI App"
                      className="w-full h-full"
                    />
                  </div>
                  <div>
                    <p className="text-white/60 text-xs">Scan to</p>
                    <p className="text-white/80 text-sm font-medium">Download App</p>
                  </div>
                </div>
              </li>
              <li className="pt-6">
                <h4 className="text-white font-medium mb-2">Newsletter</h4>
                <form onSubmit={handleNewsletterSubscribe} className="flex mt-2">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    disabled={isSubscribing}
                    className="bg-white/10 text-white px-4 py-2 rounded-l-md focus:outline-none focus:bg-white/20 flex-1 disabled:opacity-50 placeholder:text-white/50"
                  />
                  <button
                    type="submit"
                    disabled={isSubscribing}
                    className="bg-dineai-yellow text-black px-4 py-2 font-medium rounded-r-md hover:bg-dineai-yellow/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubscribing ? 'Subscribing...' : 'Subscribe'}
                  </button>
                </form>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-white/60 text-sm mb-4 md:mb-0">
            &copy; {currentYear} Dine AI, LLC. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <Link to="/privacy" className="text-white/60 hover:text-white text-sm transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-white/60 hover:text-white text-sm transition-colors">
              Terms of Service
            </Link>
            <Link to="/cookies" className="text-white/60 hover:text-white text-sm transition-colors">
              Cookie Policy
            </Link>
            <Link to="/accessibility" className="text-white/60 hover:text-white text-sm transition-colors">
              Accessibility
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
